import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface Apartment {
  id: string;
  name: string;
  type: string;
  price: number;
  priceUSD: number;
  image: string;
  alt: string;
  features: string[];
  maxGuests: number;
  size: string;
}

interface ApartmentSelectionProps {
  apartments: Apartment[];
  selectedApartment: string | null;
  onSelect: (id: string) => void;
  currency: 'INR' | 'USD';
}

export default function ApartmentSelection({
  apartments,
  selectedApartment,
  onSelect,
  currency,
}: ApartmentSelectionProps) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-display font-semibold text-[var(--color-text-primary)] mb-2">
          Select Your Apartment
        </h2>
        <p className="text-[var(--color-text-secondary)]">
          Choose from our premium Luxos, Primera, or Classica apartments
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {apartments.map((apartment) => (
          <div
            key={apartment.id}
            onClick={() => onSelect(apartment.id)}
            className={`relative rounded-xl overflow-hidden cursor-pointer transition-all duration-300 ${
              selectedApartment === apartment.id
                ? 'ring-4 ring-[var(--color-accent)] shadow-elevated'
                : 'ring-1 ring-[var(--color-border)] hover:ring-2 hover:ring-[var(--color-primary)] shadow-subtle'
            }`}
          >
            {selectedApartment === apartment.id && (
              <div className="absolute top-4 right-4 z-10 w-8 h-8 bg-[var(--color-accent)] rounded-full flex items-center justify-center">
                <Icon name="CheckIcon" size={20} className="text-white" />
              </div>
            )}

            <div className="relative h-48 overflow-hidden">
              <AppImage
                src={apartment.image}
                alt={apartment.alt}
                className="w-full h-full object-cover"
              />
            </div>

            <div className="p-6 bg-white">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="text-xl font-display font-semibold text-[var(--color-text-primary)]">
                    {apartment.name}
                  </h3>
                  <p className="text-sm text-[var(--color-text-secondary)] mt-1">
                    {apartment.type}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-[var(--color-accent)]">
                    {currency === 'INR' ? `₹${apartment.price.toLocaleString('en-IN')}` : `$${apartment.priceUSD}`}
                  </p>
                  <p className="text-xs text-[var(--color-text-secondary)]">per night</p>
                </div>
              </div>

              <div className="flex items-center gap-4 text-sm text-[var(--color-text-secondary)] mb-4">
                <div className="flex items-center gap-1">
                  <Icon name="UserGroupIcon" size={16} />
                  <span>{apartment.maxGuests} guests</span>
                </div>
                <div className="flex items-center gap-1">
                  <Icon name="HomeIcon" size={16} />
                  <span>{apartment.size}</span>
                </div>
              </div>

              <div className="space-y-2">
                {apartment.features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-2 text-sm text-[var(--color-text-secondary)]">
                    <Icon name="CheckCircleIcon" size={16} className="text-[var(--color-success)]" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}